import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDraftResumeToast } from "@/hooks/useDraftResumeToast";
import { useCurrency } from "@/contexts/CurrencyContext";

import { Plus, Trash2, UserPlus, Phone, Mail, CalendarDays, Ban, CalendarClock, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { AddTrialProspectDialog } from "@/components/clients/AddTrialProspectDialog";
import { RescheduleTrialDialog } from "@/pages/admin/components/RescheduleTrialDialog";
import {
  useTrialProspects, useTrialStats, useTrialProspectMutations,
} from "@/hooks/useTrialProspects";
import type { TrialProspect, TrialStatus } from "@/services/edgeFunctionWrappers/trialProspectsWrapper";
import { buildPrefillPreview } from "@/lib/trialPrefillPreview";
import { useUserStore } from "@/store/userStore";
import { canChooseSubscriptionOnConvert, visibleTrialActions } from "@/lib/trials/permissions";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

const STATUS_COLORS: Record<TrialStatus, string> = {
  inscrit: "hsl(142 76% 36%)",
  non_inscrit: "hsl(0 84% 60%)",
  en_attente: "hsl(48 96% 53%)",
  absence: "hsl(215 16% 47%)",
};

const STATUS_CLASSES: Record<TrialStatus, string> = {
  inscrit: "trial-status trial-status--registered",
  non_inscrit: "trial-status trial-status--declined",
  en_attente: "trial-status trial-status--pending",
  absence: "trial-status trial-status--absent",
};

function StatusBadge({ status, premium = false }: { status: TrialStatus; premium?: boolean }) {
  const { t } = useTranslation("trials");
  return (
    <Badge className={premium ? STATUS_CLASSES[status] : undefined} style={premium ? undefined : { backgroundColor: STATUS_COLORS[status], color: "white" }}>
      {t(`statuses.${status}`)}
    </Badge>
  );
}

function StatusSelect({ value, onChange, compact }: { value: TrialStatus; onChange: (v: TrialStatus) => void; compact?: boolean }) {
  const { t } = useTranslation("trials");
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TrialStatus)}>
      <SelectTrigger className={compact ? "h-8 w-full" : "h-8 w-[150px]"}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="en_attente">{t("statuses.en_attente")}</SelectItem>
        <SelectItem value="inscrit">{t("statuses.inscrit")}</SelectItem>
        <SelectItem value="non_inscrit">{t("statuses.non_inscrit")}</SelectItem>
        <SelectItem value="absence">{t("statuses.absence")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

export default function TrialsPage() {
  const { t } = useTranslation(["trials", "adminui3"]);
  const gymId = useUserStore(s => s.gymId);
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  // Saisie prospect interrompue par un reload → proposer de la reprendre.
  useDraftResumeToast([
    {
      formId: "add-trial-prospect",
      message: t("trialsPage.draftUnsaved", { ns: "adminui3" }),
      description: (d) =>
        [d.firstName, d.lastName].filter(Boolean).join(" ") || (d.email as string) || undefined,
      isEmpty: (d) => !(d.firstName || d.lastName || d.email || d.phone),
      onResume: () => setAddOpen(true),
    },
  ]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | TrialStatus>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [deleting, setDeleting] = useState<TrialProspect | null>(null);
  const [converting, setConverting] = useState<TrialProspect | null>(null);
  const [cancelling, setCancelling] = useState<TrialProspect | null>(null);
  const [rescheduling, setRescheduling] = useState<TrialProspect | null>(null);

  const filters = useMemo(() => ({
    status: statusFilter === "all" ? undefined : statusFilter,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    search: search.trim() || undefined,
  }), [statusFilter, startDate, endDate, search]);

  const { data: prospects = [], isLoading, isError, error, refetch, isFetching } = useTrialProspects(filters);
  const { data: stats } = useTrialStats({ start_date: startDate || undefined, end_date: endDate || undefined });
  const { updateStatus, remove, convert, cancelTrial, rescheduleTrial } = useTrialProspectMutations();

  // Ne jamais masquer un échec de chargement derrière une liste vide.
  const loadError = !gymId
    ? t("errors.noGym")
    : isError
      ? ((error as Error)?.message || t("errors.loadFailed"))
      : null;

  // Coach : cycle de vie oui, suppression non.
  const role = useUserStore(s => s.role);
  const isCoachPortal = role === "coach";
  const perms = visibleTrialActions(role ?? "");


  // Realtime: refresh list & stats when a trial prospect changes
  useEffect(() => {
    if (!gymId) return;
    const ch = supabase
      .channel(`admin-trials-${gymId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "trial_prospects", filter: `gym_id=eq.${gymId}` },
        () => {
          qc.invalidateQueries({ queryKey: ["trial-prospects"] });
          qc.invalidateQueries({ queryKey: ["trial-stats"] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [gymId, qc]);

  const onChangeStatus = async (p: TrialProspect, status: TrialStatus) => {
    try {
      await updateStatus.mutateAsync({ id: p.id, status });
      toast.success(t("toasts.statusUpdated"));
    } catch (e: any) {
      toast.error(e?.message ?? "Erreur");
    }
  };

  const onDelete = async () => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success(t("toasts.deleted"));
      setDeleting(null);
    } catch (e: any) { toast.error(e?.message ?? "Erreur"); }
  };

  const onCancel = async () => {
    if (!cancelling) return;
    try {
      await cancelTrial.mutateAsync(cancelling.id);
      toast.success(t("toasts.cancelled"));
      setCancelling(null);
    } catch (e: any) { toast.error(e?.message ?? "Erreur"); }
  };


  return (
    <div className={cn("container mx-auto p-3 sm:p-4 space-y-4", isCoachPortal && "coach-trials-page")}> 
      <div className={cn("flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4", isCoachPortal && "coach-trials-heading")}>
        <div>
          <h1 className="text-2xl font-bold sm:text-3xl">{t("title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">{t("subtitle")}</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className={cn("w-full sm:w-auto", isCoachPortal && "coach-primary-cta coach-trial-add")}> 
          <Plus className="mr-2 h-4 w-4" /> {t("addProspect")}
        </Button>
      </div>

      <Tabs defaultValue="list" className={cn(isCoachPortal && "coach-trials-content")}>
        <TabsList className={cn("w-full grid grid-cols-2 sm:w-auto sm:inline-flex", isCoachPortal && "coach-trials-tabs")}> 
          <TabsTrigger value="list">{t("tabs.list")}</TabsTrigger>
          <TabsTrigger value="stats">{t("tabs.stats")}</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-3">
          {loadError && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="text-sm text-destructive">{loadError}</div>
                <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
                  {t("errors.retry")}
                </Button>
              </CardContent>
            </Card>
          )}
          <Card className={cn(isCoachPortal && "coach-filter-card")}> 
            <CardContent className="grid grid-cols-1 gap-3 pt-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-10" placeholder={t("filters.search")} value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("filters.all")}</SelectItem>
                  <SelectItem value="en_attente">{t("statuses.en_attente")}</SelectItem>
                  <SelectItem value="inscrit">{t("statuses.inscrit")}</SelectItem>
                  <SelectItem value="non_inscrit">{t("statuses.non_inscrit")}</SelectItem>
                  <SelectItem value="absence">{t("statuses.absence")}</SelectItem>
                </SelectContent>
              </Select>
              <Input className={cn(isCoachPortal && "coach-trial-date-filter")} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              <Input className={cn(isCoachPortal && "coach-trial-date-filter")} type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </CardContent>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {isLoading && (
              <div className="text-center py-8 text-muted-foreground text-sm">…</div>
            )}
            {!isLoading && !loadError && prospects.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">{t("errors.empty")}</div>
            )}
            {prospects.map(p => (
              <Card key={p.id} className={cn("rounded-md", isCoachPortal && "coach-prospect-card")}> 
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-bold">{p.first_name} {p.last_name}</div>
                      <div className="mt-1 flex items-center gap-2 truncate text-xs text-muted-foreground">
                        <CalendarDays className="h-4 w-4 shrink-0 text-foreground" />
                        {p.class_date} {p.time ? `· ${p.time}` : ""}
                        {p.scheduled_classes?.name ? ` · ${p.scheduled_classes.name}` : ""}
                      </div>
                    </div>
                    <StatusBadge status={p.status} premium={isCoachPortal} />
                  </div>

                  <div className="space-y-2 text-sm">
                    <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-primary hover:underline truncate">
                      <Mail className="h-4 w-4 shrink-0" /> <span className="truncate">{p.email}</span>
                    </a>
                    {p.phone && (
                      <a href={`tel:${p.phone}`} className="flex items-center gap-2 text-primary hover:underline">
                        <Phone className="h-4 w-4 shrink-0" /> {p.phone}
                      </a>
                    )}
                  </div>

                  <div className="coach-prospect-actions flex items-center gap-2 border-t border-border pt-3">
                    <div className="flex-1 min-w-[120px]">
                      <StatusSelect value={p.status} onChange={(s) => onChangeStatus(p, s)} compact />
                    </div>
                    <Button size="icon" variant="outline" onClick={() => setRescheduling(p)} title={t("actionsLabels.reschedule")}>
                      <CalendarClock className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" disabled={p.status === "absence"} onClick={() => setCancelling(p)} title={t("actionsLabels.cancelClass")}>
                      <Ban className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" disabled={!!p.converted_client_id} onClick={() => setConverting(p)} title={t("actionsLabels.convert")}>
                      <UserPlus className="h-4 w-4" />
                    </Button>
                    {perms.remove && (
                      <Button size="sm" variant="ghost" onClick={() => setDeleting(p)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop table */}
          <Card className={cn("hidden md:block", isCoachPortal && "coach-prospect-table")}> 
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("firstName")} {t("lastName")}</TableHead>
                    <TableHead>{t("email")}</TableHead>
                    <TableHead>{t("phone")}</TableHead>
                    <TableHead>{t("class")}</TableHead>
                    <TableHead>{t("date")}</TableHead>
                    <TableHead>{t("status")}</TableHead>
                    <TableHead className="text-right">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">…</TableCell></TableRow>
                  )}
                  {!isLoading && !loadError && prospects.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{t("errors.empty")}</TableCell></TableRow>
                  )}
                  {prospects.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.first_name} {p.last_name}</TableCell>
                      <TableCell>{p.email}</TableCell>
                      <TableCell>{p.phone ?? "—"}</TableCell>
                      <TableCell>{p.scheduled_classes?.name ?? "—"}</TableCell>
                      <TableCell>{p.class_date} {p.time ?? ""}</TableCell>
                      <TableCell>
                        <StatusSelect value={p.status} onChange={(s) => onChangeStatus(p, s)} />
                      </TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => setRescheduling(p)} title={t("actionsLabels.reschedule")}>
                          <CalendarClock className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" disabled={p.status === "absence"} onClick={() => setCancelling(p)} title={t("actionsLabels.cancelClass")}>
                          <Ban className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" disabled={!!p.converted_client_id} onClick={() => setConverting(p)}>
                          <UserPlus className="h-4 w-4" />
                        </Button>
                        {perms.remove && (
                          <Button size="sm" variant="ghost" onClick={() => setDeleting(p)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats">
          <StatsView stats={stats} startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        </TabsContent>
      </Tabs>

      <AddTrialProspectDialog open={addOpen} onOpenChange={setAddOpen} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmDelete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirmDeleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>{t("actionsLabels.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConvertDialog
        prospect={converting}
        isSubmitting={convert.isPending}
        allowSubscription={canChooseSubscriptionOnConvert(role ?? "")}
        onClose={() => { if (!convert.isPending) setConverting(null); }}
        onDone={async (subId) => {
          if (!converting || convert.isPending) return;
          try {
            // Un coach n'envoie JAMAIS subscription_type_id (l'edge le
            // refuserait avec SUBSCRIPTION_ADMIN_ONLY).
            const subscriptionTypeId = canChooseSubscriptionOnConvert(role ?? "") ? subId || undefined : undefined;
            await convert.mutateAsync({ id: converting.id, subscription_type_id: subscriptionTypeId });
            toast.success(
              canChooseSubscriptionOnConvert(role ?? "")
                ? t("toasts.converted")
                : t("convertDialog.coachConverted", "Client créé — l'administrateur a été notifié pour finaliser l'abonnement.")
            );
            setConverting(null);
          } catch (e: any) { toast.error(e?.message ?? "Erreur"); }
        }}
      />


      <AlertDialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmCancel")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirmCancelDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel}>{t("actionsLabels.cancelClass")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RescheduleTrialDialog
        prospect={rescheduling}
        submitting={rescheduleTrial.isPending}
        onClose={() => setRescheduling(null)}
        onDone={async (new_class_id, new_class_date) => {
          if (!rescheduling) return;
          try {
            await rescheduleTrial.mutateAsync({ id: rescheduling.id, new_class_id, new_class_date });
            toast.success(t("toasts.rescheduled"));
            setRescheduling(null);
          } catch (e: any) {
            if (String(e?.message ?? "").includes("CLASS_FULL")) {
              toast.error(t("toasts.classFull"));
            } else {
              toast.error(e?.message ?? "Erreur");
            }
          }
        }}
      />
    </div>
  );
}


function StatsView({ stats, startDate, endDate, setStartDate, setEndDate }: {
  stats: any; startDate: string; endDate: string;
  setStartDate: (v: string) => void; setEndDate: (v: string) => void;
}) {
  const { t } = useTranslation("trials");
  const counts = stats?.counts ?? { inscrit: 0, non_inscrit: 0, en_attente: 0, absence: 0 };
  const total = stats?.total ?? 0;
  const rate = stats?.conversion_rate ?? 0;
  const qualitative = rate >= 75 ? "excellent" : rate >= 50 ? "good" : rate >= 25 ? "average" : "low";

  const data = (["inscrit", "non_inscrit", "en_attente", "absence"] as TrialStatus[]).map(k => ({
    name: t(`statuses.${k}`), value: counts[k] ?? 0, color: STATUS_COLORS[k],
  }));

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">{t("filters.startDate")}</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t("filters.endDate")}</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label={t("stats.total")} value={total} />
        <KpiCard label={t("statuses.inscrit")} value={counts.inscrit} color={STATUS_COLORS.inscrit} />
        <KpiCard label={t("statuses.non_inscrit")} value={counts.non_inscrit} color={STATUS_COLORS.non_inscrit} />
        <KpiCard label={t("statuses.en_attente")} value={counts.en_attente} color={STATUS_COLORS.en_attente} />
        <KpiCard label={t("statuses.absence")} value={counts.absence} color={STATUS_COLORS.absence} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">{t("stats.distribution")}</CardTitle></CardHeader>
          <CardContent className="min-h-[260px]" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {data.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">{t("stats.conversionRate")}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-4xl sm:text-5xl font-bold">{rate}%</div>
            <div className="text-sm text-muted-foreground mt-2">{t("stats.conversionFormula")}</div>
            <div className="mt-4 text-sm font-medium">{t(`stats.qualitative.${qualitative}`)}</div>
            <div className="mt-4 text-xs text-muted-foreground">
              {counts.inscrit} / ({total} − {counts.absence}) ={" "}
              {total - counts.absence > 0 ? ((counts.inscrit / (total - counts.absence)) * 100).toFixed(1) : "0"}%
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="text-xs text-muted-foreground truncate">{label}</div>
        <div className="text-2xl font-bold" style={color ? { color } : undefined}>{value}</div>
      </CardContent>
    </Card>
  );
}

function ConvertDialog({ prospect, onClose, onDone, isSubmitting = false, allowSubscription = true }: {
  prospect: TrialProspect | null; onClose: () => void; onDone: (subId: string) => void; isSubmitting?: boolean;
  /** false pour le coach : pas de sélecteur d'abonnement (l'admin finalise). */
  allowSubscription?: boolean;
}) {

  const { t } = useTranslation("trials");
  const { formatCurrency } = useCurrency();
  const gymId = useUserStore(s => s.gymId);
  const [subId, setSubId] = useState<string>("");

  const [types, setTypes] = useState<{ id: string; name: string; price: number }[]>([]);

  useEffect(() => {
    if (!prospect || !gymId || !allowSubscription) return;
    (async () => {
      const { data } = await supabase
        .from("subscription_types")
        .select("id,name,price")
        .eq("gym_id", gymId)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      setTypes(data ?? []);
    })();
  }, [prospect, gymId, allowSubscription]);

  if (!prospect) return null;

  return (
    <Dialog open={!!prospect} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("convertDialog.title")}</DialogTitle>
          <DialogDescription>{t("convertDialog.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm">
            <strong>{prospect.first_name} {prospect.last_name}</strong> · {prospect.email}
          </div>
          {(() => {
            const prefillItems = buildPrefillPreview(prospect);
            return (
              <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground">{t("convertDialog.prefillTitle")}</div>
                {prefillItems.length === 0 ? (
                  <div className="text-sm text-muted-foreground">{t("convertDialog.prefillEmpty")}</div>
                ) : (
                  <ul className="space-y-1">
                    {prefillItems.map(item => (
                      <li key={item.key} className="text-sm">
                        <span className="font-medium">{t(`convertDialog.prefillFields.${item.key}`)}</span>
                        <span className="text-muted-foreground"> : </span>
                        <span className="whitespace-pre-line">{item.value}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })()}
          {allowSubscription ? (
            <div>
              <label className="text-sm">{t("convertDialog.subscription")}</label>
              <Select value={subId} onValueChange={setSubId}>
                <SelectTrigger><SelectValue placeholder={t("convertDialog.none")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("convertDialog.none")}</SelectItem>
                  {types.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} — {formatCurrency(s.price)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="text-sm rounded-md border p-3 bg-muted/30 text-muted-foreground">
              {t("convertDialog.coachInfo", "L'abonnement et le paiement seront finalisés par l'administrateur, qui sera notifié.")}
            </div>
          )}
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">{t("cancel")}</Button>
          <Button onClick={() => onDone(subId === "__none__" ? "" : subId)} disabled={isSubmitting} className="w-full sm:w-auto">
            {isSubmitting ? "…" : t("convertDialog.confirm")}
          </Button>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  );
}


